// The gallery's data hook: drives the `api` client through cursor pagination and
// keeps the accumulated, newest-first list. Thin over the client — all request
// shaping/error normalization lives in `api`; this only sequences calls + holds
// React state. Kept in its own module so it is unit-testable with a stub client.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  type ApiClient,
  type MediaItem,
  type MediaCounts,
  type MediaTypeFilter,
  ApiError,
  sortNewestFirst,
} from "./api";
import { isClip } from "./cores";

export interface UseMediaState {
  items: MediaItem[];
  /** True only during the very first page load (the full-screen loader). */
  loading: boolean;
  /** True while a "load more" page is in flight. */
  loadingMore: boolean;
  /**
   * A page-1 re-fetch AFTER the first load (a filter change or reload). Distinct from
   * `loading` so the gallery keeps the filter tabs + the previous items on screen
   * instead of flashing the full-screen loader (the tabs must not disappear).
   */
  refetching: boolean;
  /** The continuation cursor; non-null ⇢ a "load more" page is available. */
  cursor: string | null;
  /** A non-auth error message to surface (auth is handled via `unauthorized`). */
  error: string | null;
  /** The Owner Session is missing/expired (ADR-0060) — show the re-auth affordance. */
  unauthorized: boolean;
  /** The filtered library size from the server (undefined until the first page lands). */
  total?: number;
  /** Per-type whole-library counts from the server (undefined until the first page lands). */
  counts?: MediaCounts;
  /** How many items are Archived (ADR-0058) — the "Archived (N)" toggle badge (undefined until the first page). */
  archivedCount?: number;
  /** Fetch the next page (no-op when none remains or one is already loading). */
  loadMore: () => void;
  /** Reload page 1 (keeps the current items until the fresh page lands). */
  reload: () => void;
  /**
   * An archive/restore moved `keys` OUT of the current view (ADR-0058): drop them from the
   * loaded list in place and fix up the local counts. Deliberately NOT a page-1 reload —
   * that collapsed the accumulated pages back to one and threw away the owner's scroll
   * position mid-triage. The next natural fetch re-syncs with the server.
   */
  dropItems: (keys: string[]) => void;
}

/**
 * Manage the paginated, newest-first media list for the gallery. The list is
 * re-sorted newest-first on every append because the Worker lists R2
 * lexicographically, not chronologically (ADR-0022).
 */
export function useMedia(
  client: ApiClient,
  filter: MediaTypeFilter = "all",
  archived = false,
): UseMediaState {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [counts, setCounts] = useState<MediaCounts | undefined>(undefined);
  const [archivedCount, setArchivedCount] = useState<number | undefined>(undefined);

  // Guard against overlapping fetches and stale writes after a newer fetch supersedes.
  const inFlight = useRef(false);
  const reqId = useRef(0);
  // True once the first page has ever resolved — gates initial `loading` vs `refetching`.
  const hasLoaded = useRef(false);
  // The in-flight request's abort controller (R26): aborted on unmount / when a fetch is
  // superseded, so a stalled request doesn't wedge the guard and no state is written late.
  const abortRef = useRef<AbortController | null>(null);
  // Mounted flag so the `finally` never touches state after unmount (a hook-initiated abort
  // fires the catch/finally asynchronously, by which point this is already false).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      abortRef.current?.abort(); // cancel any in-flight page fetch on unmount
    };
  }, []);

  const fetchPage = useCallback(
    async (nextCursor: string | null, mode: "page1" | "more") => {
      if (inFlight.current) return;
      inFlight.current = true;
      const id = ++reqId.current;
      const controller = new AbortController();
      abortRef.current = controller;
      // The very first page shows the full-screen loader; a later page-1 fetch (a filter
      // change / reload) is a quiet `refetching` so the grid + tabs stay on screen.
      if (mode === "more") setLoadingMore(true);
      else if (hasLoaded.current) setRefetching(true);
      else setLoading(true);
      setError(null);

      try {
        const page = await client.getMedia({ cursor: nextCursor, type: filter, archived, signal: controller.signal });
        if (id !== reqId.current) return; // superseded by a newer fetch
        setItems((prev) =>
          sortNewestFirst(mode === "more" ? [...prev, ...page.items] : page.items),
        );
        setCursor(page.cursor);
        // Whole-library counts + filtered total + the archived badge ride every page (the Worker
        // sends them); older stubs omit them, so only update when present.
        if (page.counts) setCounts(page.counts);
        if (page.total != null) setTotal(page.total);
        if (page.archived != null) setArchivedCount(page.archived);
        hasLoaded.current = true;
      } catch (err) {
        if (id !== reqId.current) return;
        // A hook-initiated abort (unmount / supersede) is our own cancellation, not a
        // failure to surface. The client's own timeout is a DIFFERENT signal, so a stall
        // still falls through to setError below (and the guard releases in `finally`).
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.isUnauthorized) {
          setUnauthorized(true);
        } else {
          setError(err instanceof Error ? err.message : "failed to load media");
        }
      } finally {
        // Only the LATEST request (and only while still mounted) releases the in-flight
        // guard + clears the spinners; a superseded older request must not free the guard
        // while the newer one is running (else a Load-more in that window could fire with
        // the previous filter's cursor). Releasing here also recovers from a client-timeout
        // abort, so a stall no longer wedges `loading`/`inFlight` forever (R26).
        if (id === reqId.current && aliveRef.current) {
          setLoading(false);
          setLoadingMore(false);
          setRefetching(false);
          inFlight.current = false;
        }
      }
    },
    [client, filter, archived],
  );

  const loadMore = useCallback(() => {
    if (cursor && !inFlight.current) void fetchPage(cursor, "more");
  }, [cursor, fetchPage]);

  const dropItems = useCallback(
    (keys: string[]) => {
      const removing = new Set(keys);
      const dropped = items.filter((it) => removing.has(it.key));
      if (dropped.length === 0) return;
      const clips = dropped.filter((it) => isClip(it.key)).length;
      const photos = dropped.length - clips;
      setItems((prev) => prev.filter((it) => !removing.has(it.key)));
      // `total`/`counts` describe the CURRENT view's set (default vs Archived are disjoint
      // — media-index visibilityClause), so a drop always decrements them; the Archived
      // badge moves the opposite way (archiving adds to it, restoring takes from it).
      setTotal((t) => (t == null ? t : Math.max(0, t - dropped.length)));
      setCounts((c) =>
        c
          ? {
              all: Math.max(0, c.all - dropped.length),
              photos: Math.max(0, c.photos - photos),
              clips: Math.max(0, c.clips - clips),
            }
          : c,
      );
      setArchivedCount((n) =>
        archived ? Math.max(0, (n ?? 0) - dropped.length) : (n ?? 0) + dropped.length,
      );
    },
    [items, archived],
  );

  const reload = useCallback(() => {
    abortRef.current?.abort(); // cancel a stalled in-flight page so it can't wedge the guard
    reqId.current++; // invalidate any in-flight page
    inFlight.current = false;
    setUnauthorized(false);
    // Keep the current items until the fresh page lands (no flash of empty / loader).
    void fetchPage(null, "page1");
  }, [fetchPage]);

  // Initial load, and a page-1 re-fetch whenever the type filter changes. Items are NOT
  // cleared first, so the filter tabs + the previous results stay on screen during the
  // swap (a filter change must not blank the gallery). The cleanup aborts a superseded
  // in-flight page so it frees its connection instead of running to the timeout (R26).
  useEffect(() => {
    reqId.current++;
    inFlight.current = false;
    void fetchPage(null, "page1");
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, filter, archived]);

  return {
    items,
    loading,
    loadingMore,
    refetching,
    cursor,
    error,
    unauthorized,
    total,
    counts,
    archivedCount,
    loadMore,
    reload,
    dropItems,
  };
}
